import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { CypressIds } from '../../../../constants/cypressIds';
import DataTypes from '../../../../constants/dataTypes';
import { CertSubmissionDuskIds } from '../../../../constants/duskIds';
import { createErrorToast } from '../../../../redux/modules/app';
import colors from '../../../../styles/theme/foundation/colors';
import CertificateCreationBlueprintType from '../../../../types/CertificateCreationBlueprintType';
import { arrayFilterAndMap, createMap } from '../../../../utils/arrayUtil';
import { getDateObject } from '../../../../utils/dateUtil';
import FormInput from '../../../form/FormInput';
import InputCaption from '../../../form/InputCaption';
import InputLabel from '../../../form/InputLabel';
import SearchableDropdownMenu from '../../../SearchableDropdownMenu';
import { getEnhancedPolicyData } from '../../util';

const policyNumberBase = {
  dataType: DataTypes.String,
  displayName: 'Policy Number',
  key: 'number',
  required: true,
  validation: { required: { value: true, message: 'Policy Number is a required field' } },
  validateWhitespace: true,
  dusk: CertSubmissionDuskIds.policyNumberInput,
  cypressId: CypressIds.policyNumberInput
};

const policySubWaivedBase = {
  dataType: DataTypes.Boolean,
  displayName: 'Subrogation Waived?',
  key: 'subrogation_waived',
  dusk: CertSubmissionDuskIds.subrogationWaivedCheckbox,
  cypressId: CypressIds.subrogationWaivedCheckbox
};

const policyAdditionalInsuredBase = {
  dataType: DataTypes.Boolean,
  displayName: 'Additional Insured?',
  key: 'additional_insured',
  dusk: CertSubmissionDuskIds.additionalInsuredCheckbox,
  cypressId: CypressIds.additionalInsuredCheckbox
};

const policyStartDateBase = {
  dataType: DataTypes.Date,
  displayName: 'Effective On',
  key: 'effective_at',
  required: true,
  validation: { required: { value: true, message: 'Effective Date is required' } },
  validateWhitespace: false,
  dusk: CertSubmissionDuskIds.effectiveOnDateInput,
  cypressId: CypressIds.policyEffectiveDateInput
};

const policyEndDateBase = {
  dataType: DataTypes.Date,
  displayName: 'Expires On',
  key: 'expires_at',
  required: true,
  validation: { required: { value: true, message: 'Expiration Date is required' } },
  validateWhitespace: false,
  dusk: CertSubmissionDuskIds.expiresOnDateInput,
  cypressId: CypressIds.policyExpirationDateInput
};

const baseProperties = [
  policyNumberBase
];

const baseBooleanProperties = [
  policyAdditionalInsuredBase,
  policySubWaivedBase
];

const baseDateProperties = [
  policyStartDateBase,
  policyEndDateBase
];

const PolicyFormGenerator = ({
  blueprint,
  policyType,
  policy,
  submitFunction = () => null,
  secondaryFunction = () => null,
  ternaryFunction = () => null,
  submitButtonLabel = 'Submit',
  secondaryButtonLabel = 'Cancel',
  ternaryButtonLabel = '',
  formName = ''
}) => {
  const dispatch = useDispatch();
  const insuranceCompanies = useSelector(blueprint.selectInsuranceCompanies);
  const insuranceCompanyPageInfo = useSelector(blueprint.selectInsuranceCompanyPageInfo);
  const policyReviewContentHeight = useSelector(blueprint.selectPolicyReviewContentHeight);
  const [insuranceCompany, setInsuranceCompany] = useState(policy?.insuranceCompany);

  const buildFormInputName = p => p.key || `properties_${p.id}`;

  const booleanProperties = useMemo(
    () => policyType.properties
      .filter(p => p.dataType === DataTypes.Boolean && !p.displayName.includes('General Aggregate Limit Applied Per')),
    [policyType]
  );

  const limitProperties = useMemo(() => arrayFilterAndMap(
    policyType.properties,
    p => p.dataType !== DataTypes.Boolean,
    p => {
      const limitProperty = { ...p };

      if (policy?.suggestedLimits && Object.keys(policy.suggestedLimits).length) {
        const propertySuggestedLimits = policy.suggestedLimits[`properties_${p.id}`];

        if (propertySuggestedLimits) {
          limitProperty.suggestedLimits = propertySuggestedLimits;
        }
      }

      return limitProperty;
    }
  ), [policy, policyType]);

  const generalAggregateProperties = useMemo(
    () => policyType.properties.filter(p => p.displayName.includes('General Aggregate Limit Applied Per'))
      .map(genAggProp => ({ ...genAggProp, displayName: genAggProp.displayName.split(':')[1] })),
    [booleanProperties]
  );

  const defaultValues = useMemo(() => {
    if (policy && Object.keys(policy).length) {
      const policyPropertyValuesMap = createMap(policy.propertyValues, 'insurancePolicyTypePropertyId');

      const baseDefaults = {
        insurance_company: insuranceCompany,
        [buildFormInputName(policyNumberBase)]: policy.number,
        [buildFormInputName(policySubWaivedBase)]: policy.subrogationWaived,
        [buildFormInputName(policyAdditionalInsuredBase)]: policy.additionalInsured,
        [buildFormInputName(policyStartDateBase)]: getDateObject(policy.effectiveAt),
        [buildFormInputName(policyEndDateBase)]: getDateObject(policy.expiresAt)
      };

      const propertyDefaults = policyType.properties.reduce((values, property) => ({
        ...values,
        [buildFormInputName(property)]: property.dataType === DataTypes.Boolean
          ? !!policyPropertyValuesMap[property.id]?.value
          : policyPropertyValuesMap[property.id]?.value || ''
      }), {});

      return { ...propertyDefaults, ...baseDefaults };
    }

    const policyBaseProperties = {
      [buildFormInputName(policyNumberBase)]: '',
      [buildFormInputName(policySubWaivedBase)]: false,
      [buildFormInputName(policyAdditionalInsuredBase)]: false,
      [buildFormInputName(policyStartDateBase)]: null,
      [buildFormInputName(policyEndDateBase)]: null
    };

    const policyProperties = limitProperties.reduce((values, property) => ({
      ...values,
      [buildFormInputName(property)]: ''
    }), {});

    return { ...policyProperties, ...policyBaseProperties };
  }, [limitProperties]);

  useEffect(() => {
    reset(defaultValues, { keepValues: false, keepDefaultValues: false, keepDirty: true });
    dispatch(blueprint.listInsuranceCompanies());
  }, [defaultValues]);

  const {
    handleSubmit,
    reset,
    unregister,
    getValues,
    setValue,
    ...useFormMethods
  } = useForm({ defaultValues });

  const onCustomInsuranceCompanyCreate = async companyDetails => {
    const company = await dispatch(blueprint.createInsuranceCompany(companyDetails));

    if (!company) {
      dispatch(createErrorToast({
        title: 'Unable to create insurance company',
        description: 'Please try again.',
        duration: 6000,
        isClosable: true
      }));

      return;
    }

    setInsuranceCompany(company);
    setValue('insurance_company', company, { shouldValidate: true });
  };

  const validateInsuranceCompanyName = companyName => {
    if (companyName.length > 64) {
      return { valid: false, message: 'Insurance Company Name must have fewer than 64 characters' };
    }

    if (companyName.length < 2) {
      return { valid: false, message: 'Insurance Company Name must have at least 2 characters' };
    }

    return { valid: true, message: '' };
  };

  const validateMinMaxDate = (value, key) => {
    const year = value.getFullYear().toString();

    if (year.length > 4) {
      return `Invalid year: ${year}`;
    }

    const formValues = getValues();

    const expiresAtFormValue = formValues.expires_at;
    const effectiveAtFormValue = formValues.effective_at;

    if (key === 'details.effective_at' && expiresAtFormValue) {
      return value >= expiresAtFormValue ? 'Effective Date must be before Expiration Date' : true;
    }

    if (key === 'details.expires_at' && effectiveAtFormValue) {
      const validEnd = value <= effectiveAtFormValue ? 'Expiration Date must be after Effective Date' : true;
      if (typeof validEnd === 'string') {
        useFormMethods.resetField('details.effective_at', { keepError: true });
        useFormMethods.resetField('details.expires_at', { keepError: true });
      }

      return validEnd;
    }

    return true;
  };

  const validateMinOnePolicyProp = enhancedPolicyData => (enhancedPolicyData.properties.length > 0);

  return (
    <Flex
      as="form"
      flexFlow="column nowrap"
      flexGrow={1}
      id={formName}
      justifyContent="space-between"
    >
      <Flex
        flexFlow="column nowrap"
        id="cert-flow-policy-details"
        overflow="auto"
        width="100%"
      >
        <Flex
          borderBottomColor={colors.greyScale['200']}
          borderBottomWidth="3px"
          flexFlow="column nowrap"
          mt={8}
        >
          <Text textStyle="bodyXxl600">{policyType.displayName}</Text>
        </Flex>

        <VStack
          alignItems="center"
          flex={`1 0 ${policyReviewContentHeight}`}
          flexFlow="column nowrap"
          mt={4}
          overflowY="scroll"
        >
          <Flex flexWrap="wrap" justifyContent="space-between" mt={4} width="100%">
            {booleanProperties.map(p => (
              <FormInput
                cypressId={CypressIds.getPolicyTypePropertyCheckbox(p.id)}
                duskSelector={CertSubmissionDuskIds.getPolicyTypePropertyCheckbox(p.id)}
                inputName={buildFormInputName(p)}
                labelName={p.displayName}
                type="switch"
                useFormMethods={useFormMethods}
                width="50%"
              />
            ))}
          </Flex>
          {!!generalAggregateProperties.length && (
            <>
              <InputLabel labelName="General Aggregate Limit Applies Per: " mb="8px" />
              <Flex flexWrap="wrap" justifyContent="space-between" mt={4} width="100%">
                {
                  generalAggregateProperties.map(p => (
                    <FormInput
                      cypressId={CypressIds.getPolicyTypePropertyCheckbox(p.id)}
                      duskSelector={CertSubmissionDuskIds.getPolicyTypePropertyCheckbox(p.id)}
                      inputName={buildFormInputName(p)}
                      labelName={p.displayName}
                      type="switch"
                      useFormMethods={useFormMethods}
                      width="50%"
                    />
                  ))
                }
              </Flex>
            </>
          )}
          <Box minHeight="92px" width="100%">
            <InputLabel required labelName="Insurance Company" />
            <Controller
              control={useFormMethods.control}
              name="insurance_company"
              render={({ field }) => (
                <SearchableDropdownMenu
                  allowCustomValues
                  customLabel="New Insurance Company"
                  customPlaceholder="Insurance Company Name"
                  customValidation={validateInsuranceCompanyName}
                  cypressId={CypressIds.certSubmissionInsuranceCompanyDropdownMenu}
                  data={insuranceCompanies}
                  duskSelector={CertSubmissionDuskIds.insuranceCompanyDropdownMenu}
                  fetchNextData={(...args) => dispatch(blueprint.listInsuranceCompanies(...args))}
                  hasMore={insuranceCompanyPageInfo?.hasMore}
                  idKey="companyId"
                  labelKey={i => (`${i.companyName}${i.naic ? ` (${i.naic})` : ''}`)}
                  maxWidth="400px"
                  minWidth="200px"
                  searchTerm={insuranceCompanies.searchFilter}
                  selected={`${insuranceCompany?.companyName}${insuranceCompany?.naic ? ` (${insuranceCompany.naic})` : ''}`}
                  showError={useFormMethods.formState?.errors?.insurance_company}
                  truncateMenuItems={false}
                  variant="primary"
                  width="100%"
                  onCustomSelect={onCustomInsuranceCompanyCreate}
                  onSearchChange={searchTerm => dispatch(blueprint.listInsuranceCompanies({ searchTerm }))}
                  onSelect={selected => {
                    field.onChange(selected);
                    setInsuranceCompany(selected);
                  }}
                  {...field}
                />
              )}
              rules={{ required: { value: true, message: 'Please select an Insurance Company' } }}
            />

            { useFormMethods.formState?.errors?.insurance_company && (
            <InputCaption caption="" error={useFormMethods.formState.errors.insurance_company} />
            )}
          </Box>
          {baseProperties.map(p => (
            <FormInput
              validateNoWhiteSpace
              cypressId={p.cypressId}
              dataType={p.dataType}
              duskSelector={p.dusk}
              inputName={buildFormInputName(p)}
              key={`${policyType.id}-${buildFormInputName(p)}`}
              labelName={p.displayName}
              placeholder={p.displayName}
              required={p?.required}
              useFormMethods={useFormMethods}
              validationRules={p?.validation}
              width="100%"
            />
          ))}
          <HStack spacing={4} width="100%">
            {baseBooleanProperties.map(p => (
              <FormInput
                cypressId={p.cypressId}
                duskSelector={p.dusk}
                inputName={buildFormInputName(p)}
                key={`${policyType.id}-${buildFormInputName(p)}`}
                labelName={p.displayName}
                type="switch"
                useFormMethods={useFormMethods}
                width="50%"
              />
            ))}
          </HStack>
          <HStack spacing={4} width="100%">
            {baseDateProperties.map(p => (
              <FormInput
                required
                cypressId={p.cypressId}
                dataType={p.dataType}
                duskSelector={p.dusk}
                inputName={buildFormInputName(p)}
                key={p.key}
                labelName={p.displayName}
                maxDate={p.key === 'details.effective_at' && useFormMethods.watch('details.expires_at')}
                minDate={p.key === 'details.expires_at' && useFormMethods.watch('details.effective_at')}
                placeholder="MM/DD/YYYY"
                useFormMethods={useFormMethods}
                validateNoWhiteSpace={false}
                validationRules={{
                  validate: value => validateMinMaxDate(value, p.key),
                  ...p.validation
                }}
                width="100%"
              />
            ))}
          </HStack>
          {limitProperties.map(p => (
            <FormInput
              cypressId={CypressIds.getPolicyTypePropertyInput(p.id)}
              dataType={p.dataType}
              duskSelector={CertSubmissionDuskIds.getPolicyTypePropertyInput(p.id)}
              inputName={buildFormInputName(p)}
              key={`${policyType.id}-${buildFormInputName(p)}`}
              labelName={p.displayName}
              placeholder={p.displayName}
              suggestedValues={p.suggestedLimits}
              useFormMethods={useFormMethods}
              width="100%"
            />
          ))}
        </VStack>
      </Flex>

      <Flex
        flex="0 0 1"
        flexFlow="column nowrap"
        id="cert-flow-policy-form-button-container"
        w="100%"
      >
        <HStack justifyContent="space-between" mt={8} w="100%">
          <Button
            data-cy={CypressIds.certSubmissionBackPolicyButton}
            dusk={CertSubmissionDuskIds.backPolicyButton}
            variant="secondary"
            onClick={() => {
              const data = getValues();

              if (policy?.suggestedLimits) {
                data.suggestedLimits = policy.suggestedLimits;
              }

              data.details = {
                additional_insured: data.additional_insured,
                effective_at: data.effective_at,
                expires_at: data.expires_at,
                number: data.number,
                subrogation_waived: data.subrogation_waived
              };

              secondaryFunction(getEnhancedPolicyData(policyType, insuranceCompany, data));
            }}
          >
            {secondaryButtonLabel}
          </Button>

          <HStack>
            <Button
              data-cy={CypressIds.certSubmissionSkipPolicyButton}
              dusk={CertSubmissionDuskIds.skipPolicyButton}
              variant="secondary"
              visibility={ternaryButtonLabel ? 'visible' : 'hidden'}
              onClick={() => {
                const data = getValues();

                if (policy?.suggestedLimits) {
                  data.suggestedLimits = policy.suggestedLimits;
                }

                data.details = {
                  additional_insured: data.additional_insured,
                  effective_at: data.effective_at,
                  expires_at: data.expires_at,
                  number: data.number,
                  subrogation_waived: data.subrogation_waived
                };

                ternaryFunction(getEnhancedPolicyData(policyType, insuranceCompany, data));
              }}
            >
              {ternaryButtonLabel}
            </Button>
            <Button
              data-cy={CypressIds.certSubmissionNextPolicyButton}
              dusk={CertSubmissionDuskIds.nextPolicyButton}
              form={formName}
              variant="gradientPrimary"
              onClick={handleSubmit(data => {
                if (policy?.suggestedLimits) {
                  data.suggestedLimits = policy.suggestedLimits;
                }

                data.details = {
                  additional_insured: data.additional_insured,
                  effective_at: data.effective_at,
                  expires_at: data.expires_at,
                  number: data.number,
                  subrogation_waived: data.subrogation_waived
                };

                const enhancedData = getEnhancedPolicyData(policyType, insuranceCompany, data);

                const validated = validateMinOnePolicyProp(enhancedData);

                if (validated) {
                  submitFunction(enhancedData);
                } else {
                  const policyPropertyNames = policyType.properties.map(p => p.displayName);
                  const policyPropertyNamesString = policyPropertyNames.join(' • ');

                  dispatch(createErrorToast(
                    {
                      title: 'Add a value to at least one of the following fields to continue:',
                      description: policyPropertyNamesString,
                      duration: 6000,
                      isClosable: true
                    }
                  ));
                }
              })}
            >
              {submitButtonLabel}
            </Button>
          </HStack>
        </HStack>
      </Flex>
    </Flex>
  );
};

PolicyFormGenerator.propTypes = {
  blueprint: CertificateCreationBlueprintType,
  policyType: PropTypes.shape({
    id: PropTypes.number,
    displayName: PropTypes.string,
    properties: PropTypes.arrayOf(PropTypes.shape({
      displayName: PropTypes.string,
      dataType: PropTypes.string
    }))
  }).isRequired,
  policy: PropTypes.shape({
    number: PropTypes.string,
    subrogationWaived: PropTypes.bool,
    additionalInsured: PropTypes.bool,
    effectiveAt: PropTypes.string,
    expiresAt: PropTypes.string,
    insuranceCompanyId: PropTypes.number,
    insuranceCompany: PropTypes.shape({ companyName: PropTypes.string }),
    propertyValues: PropTypes.arrayOf(PropTypes.shape({ insurancePolicyTypePropertyId: PropTypes.number })),
    suggestedLimits: PropTypes.arrayOf(PropTypes.shape({}))
  }),
  submitFunction: PropTypes.func,
  secondaryFunction: PropTypes.func,
  ternaryFunction: PropTypes.func,
  submitButtonLabel: PropTypes.string,
  secondaryButtonLabel: PropTypes.string,
  ternaryButtonLabel: PropTypes.string,
  formName: PropTypes.string
};

export default PolicyFormGenerator;
